import { round } from "lodash";
import { FieldErrors } from "react-hook-form";

import {
  GuidanceFileType,
  SessionInferenceMode,
} from "@/components/graphql/schema.graphql";
import { FilepickerFile, Source } from "@/components/ui-v2/FilePicker/types";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";
import {
  ContentType,
  GuidanceFileTypeSupport,
  JSONObject,
  StyleCapabilities,
} from "@/types";
import { getImageObject } from "@/utils/images";
import { getObjectType } from "@/utils/libraryObjectUtils";
import { INFERENCE_MODE_TO_MODE_TYPE } from "@/utils/styles/constants";

import type { ReferenceImageType } from "../../ui-v2/References/types";
import {
  DEFAULT_DENSITY,
  LEGACY_AUTO_DETECT_PROMPT_LANGUAGE_VALUES,
  NO_TRANSLATION_PROMPT_LANGUAGE,
} from "../constants";

export const formatSessionApiJsonField = (
  value: unknown,
): JSONObject | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as JSONObject)
    : undefined;

const EDITOR_MODES: SessionInferenceMode[] = [
  "REALTIME",
  "CANVAS_2_D",
  "CANVAS_PROMPT_EDIT",
];
export const isSessionEditorMode = (mode: SessionInferenceMode) =>
  EDITOR_MODES.includes(mode);

const CANVAS_MODES: SessionInferenceMode[] = [
  "CANVAS_2_D",
  "CANVAS_PROMPT_EDIT",
];
export const isSessionCanvasMode = (mode: SessionInferenceMode) =>
  CANVAS_MODES.includes(mode);

export const REFINEMENT_MODES: SessionInferenceMode[] = [
  // 2D
  "UPSCALE",
  "REFRAME_IMAGE",
  "VECTORIZE_IMAGE",
  "REMOVE_BACKGROUND",
  // Video
  "UPSCALE_VIDEO",
  "REFRAME_VIDEO",
  "ADD_SFX_VIDEO",
  "REMOVE_BACKGROUND_VIDEO",
  // 3D
  "RETEXTURE_MESH",
  "RIG_MESH",
  "REMESH",
];
export type SessionRefinementMode = (typeof REFINEMENT_MODES)[number];
export const isSessionRefinementMode = (mode: SessionInferenceMode) =>
  REFINEMENT_MODES.includes(mode);

export const PROMPT_EDIT_MODES: SessionInferenceMode[] = [
  "PROMPT_EDIT_IMAGE",
  "PROMPT_EDIT_VIDEO",
];
export const isSessionPromptEditMode = (mode: SessionInferenceMode) =>
  PROMPT_EDIT_MODES.includes(mode);

export const clip = (input: number, min: number, max: number) => {
  return Math.min(Math.max(input, min), max);
};

export const getVideoObject = (videoUrl: string): Promise<HTMLVideoElement> =>
  new Promise<HTMLVideoElement>((resolve, reject) => {
    const video = document.createElement("video");
    video.onloadedmetadata = () => {
      // Ensure width and height are available
      if (video.videoWidth && video.videoHeight) {
        resolve(video);
      } else {
        reject(new Error("Video dimensions not available"));
      }
    };
    video.onerror = (err) => reject(err);

    // Handle data URLs by converting to blob URL
    if (videoUrl.startsWith("data:")) {
      try {
        // Extract base64 data
        const base64Data = videoUrl.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "video/mp4" });
        const blobUrl = URL.createObjectURL(blob);

        video.src = blobUrl;
        // Clean up the blob URL when the video loads
        video.onloadedmetadata = () => {
          if (video.videoWidth && video.videoHeight) {
            URL.revokeObjectURL(blobUrl);
            resolve(video);
          } else {
            URL.revokeObjectURL(blobUrl);
            reject(new Error("Video dimensions not available"));
          }
        };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        reject(new Error(`Failed to process data URL: ${message}`));
      }
    } else {
      // Handle regular URLs
      video.crossOrigin = "anonymous";
      video.src = videoUrl;
    }
  });

export const getImageReferenceFromDownloadUrl = async (
  downloadUrl: string,
  driveFileId?: string,
): Promise<ReferenceImageType> => {
  const htmlImageObject = await getImageObject(downloadUrl);
  return {
    src: downloadUrl,
    width: htmlImageObject.width,
    height: htmlImageObject.height,
    driveFileId,
  };
};

export const getWeightFromPercentage = (percentage: number) => {
  return Math.round((percentage / 100) * 100) / 100;
};
export const getPercentageFromWeight = (weight: number) => {
  return Math.round(weight * 100);
};
export const getWeightFromSimilarity = (similarity: number) => {
  return Math.round((similarity / 100) * 20) / 20;
};
export const getSimilarityFromWeight = (weight: number) => {
  return Math.round(weight * 100);
};

export const clampPixelDensity = (pixelDensity: number): number => {
  if (!pixelDensity) return DEFAULT_DENSITY;
  return round(Math.min(Math.max(pixelDensity, 1), 4), 2);
};

export const formatAPIPromptLanguageForForm = (promptLanguage: string) =>
  [
    "en",
    "en-US",
    "en-GB",
    ...LEGACY_AUTO_DETECT_PROMPT_LANGUAGE_VALUES,
  ].includes(promptLanguage)
    ? NO_TRANSLATION_PROMPT_LANGUAGE
    : promptLanguage;

export const convertSessionFileToFilepickerFile = (
  file: SessionFileFragment,
): FilepickerFile => {
  const objectType = getObjectType(file);

  return {
    id: file.id,
    src: file.url,
    name: file.name,
    type: objectType,
    contentType: file.contentType as ContentType,
    thumbnailSrc: file.url,
    interactedAt: null,
    source: "upload" as Source,
    width: file.metadata?.width,
    height: file.metadata?.height,
  };
};

export const resolveGuidanceFileTypes = (
  capabilities: StyleCapabilities | undefined,
  inferenceMode: SessionInferenceMode,
): GuidanceFileTypeSupport[] => {
  const inferenceModeType = INFERENCE_MODE_TO_MODE_TYPE[inferenceMode];

  return (
    capabilities?.guidanceFileTypesByMode?.[
      inferenceModeType as unknown as SessionInferenceMode
    ] ??
    capabilities?.guidanceFileTypes ??
    []
  );
};

export const getGuidanceFileTypeSupport = (
  capabilities: StyleCapabilities | undefined,
  guidanceFileType: GuidanceFileType,
  inferenceMode: SessionInferenceMode,
): GuidanceFileTypeSupport | undefined => {
  const guidanceFileTypes = resolveGuidanceFileTypes(
    capabilities,
    inferenceMode,
  );

  return guidanceFileTypes?.find(
    (typeSupport) => typeSupport.type === guidanceFileType,
  );
};

export const supportsGuidanceFileType = (
  capabilities: StyleCapabilities | undefined,
  guidanceFileType: GuidanceFileType,
  inferenceMode: SessionInferenceMode,
): boolean => {
  const support = getGuidanceFileTypeSupport(
    capabilities,
    guidanceFileType,
    inferenceMode,
  );

  return Boolean(support);
};

export const getFormattedFieldErrorsMessages = (fieldErrors: FieldErrors) =>
  Object.values(fieldErrors)
    .map((fieldError) => fieldError.message)
    .filter((message): message is string => Boolean(message))
    .join("\n");
